/**
 * User Agent parsing utilities for detecting operating systems and device types
 */

import { UAParser } from "ua-parser-js";

// Operating System enum for user agent detection
export enum UserAgentOperatingSystem {
  IOS = "ios",
  MACOS = "macos",
  ANDROID_OS = "androidos",
  WINDOWS_OS = "windowsos",
  OTHER = "other",
}

/**
 * Parses a user agent string to detect the operating system
 * Uses UAParser for robust parsing, then normalizes to business-friendly categories
 * Based on analysis showing: iOS (36.25%), macOS (21.92%), Windows (21.79%), Android (17.69%)
 */
export function parseUserAgentToOS(
  userAgent: string | null | undefined
): UserAgentOperatingSystem | null {
  if (!userAgent || userAgent.trim() === "") {
    return null;
  }

  const ua = new UAParser(userAgent);
  const osName = ua.getOS().name?.toLowerCase();
  const deviceType = ua.getDevice().type;

  if (!osName) {
    return null;
  }

  // Check in order of frequency for performance
  if (osName.includes("ios")) {
    return UserAgentOperatingSystem.IOS;
  }
  if (osName.includes("android")) {
    return UserAgentOperatingSystem.ANDROID_OS;
  }
  // Handle Firefox mobile edge case: UAParser sometimes detects Firefox mobile as "Firefox OS"
  // but it's actually Android when device type is mobile and no other OS indicators
  if (
    osName.includes("firefox os") &&
    deviceType === "mobile" &&
    !userAgent.toLowerCase().includes("iphone") &&
    !userAgent.toLowerCase().includes("ipad") &&
    !userAgent.toLowerCase().includes("windows") &&
    !userAgent.toLowerCase().includes("mac")
  ) {
    return UserAgentOperatingSystem.ANDROID_OS;
  }
  if (osName.includes("mac")) {
    return UserAgentOperatingSystem.MACOS;
  }
  if (osName.includes("windows")) {
    return UserAgentOperatingSystem.WINDOWS_OS;
  }

  return UserAgentOperatingSystem.OTHER;
}
